import { RANK_NAME, SUIT_SYM, VAL } from '../scoring/ScoringConstants.js';

/**
 * Card Model representing a single playing card in Crazy Cribbage.
 */
export class Card {
  constructor(data = {}, suit = null) {
    if (typeof data === 'number' || (typeof data === 'string' && !isNaN(Number(data)) && data !== '')) {
      data = { r: Number(data), s: suit || 'S' };
    }
    this.id = String(data.id || (data.r && data.s ? `${data.r}${data.s}` : ''));
    this.r = Number(data.r || 0);
    this.s = String(data.s || 'S').toUpperCase();
    this.wild = Boolean(data.wild);
    this.playAs = data.playAs ? { r: Number(data.playAs.r), s: String(data.playAs.s).toUpperCase() } : null;
    this.masked = Boolean(data.masked);
    this.pegBadge = data.pegBadge ? String(data.pegBadge) : null;
    this.pegBadgeBonus = Boolean(data.pegBadgeBonus);
  }

  get rank() {
    return this.playAs && this.playAs.r ? this.playAs.r : this.r;
  }

  get rawRank() {
    return this.r;
  }

  get suit() {
    return this.playAs && this.playAs.s ? this.playAs.s : this.s;
  }

  get rawSuit() {
    return this.s;
  }

  get val() {
    return VAL(this.rank);
  }

  get isWild() {
    return this.wild;
  }

  get isAssignedWild() {
    return this.wild && Boolean(this.playAs);
  }

  get isRed() {
    const s = this.suit;
    return s === 'H' || s === 'D';
  }

  get isBlack() {
    const s = this.suit;
    return s === 'S' || s === 'C';
  }

  get suitSymbol() {
    return SUIT_SYM[this.suit] || '';
  }

  get rankName() {
    return RANK_NAME[this.rank] || (this.wild ? '★' : '?');
  }

  get label() {
    if (this.wild && !this.playAs) return 'Joker';
    return (RANK_NAME[this.rank] || '?') + (SUIT_SYM[this.suit] || '');
  }

  fits(currentCount = 0) {
    if (this.wild && !this.playAs) return currentCount <= 30;
    return currentCount + this.val <= 31;
  }

  assignWild(r, s) {
    this.playAs = { r: Number(r), s: String(s || this.s).toUpperCase() };
    return this;
  }

  resetWild() {
    this.playAs = null;
    return this;
  }

  clone() {
    return new Card({
      id: this.id,
      r: this.r,
      s: this.s,
      wild: this.wild,
      playAs: this.playAs ? { ...this.playAs } : null,
      masked: this.masked,
      pegBadge: this.pegBadge,
      pegBadgeBonus: this.pegBadgeBonus
    });
  }

  toPlain() {
    return {
      id: this.id,
      r: this.r,
      s: this.s,
      wild: this.wild,
      playAs: this.playAs ? { ...this.playAs } : null,
      masked: this.masked,
      pegBadge: this.pegBadge,
      pegBadgeBonus: this.pegBadgeBonus
    };
  }

  static from(obj) {
    if (!obj) return null;
    if (obj instanceof Card) return obj;
    return new Card(obj);
  }
}
